import type { ReactElement, ReactNode } from 'react';

/**
 * 主要画面に必ず用意する5状態（指示書 §7-5）:
 * 正常 / 読込中(スケルトン) / 空(イラスト+CTA) / 通信失敗(再試行) / 権限不足
 */

export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }): ReactElement {
  return (
    <div role="status" aria-label="読み込み中" className="animate-pulse">
      <span className="sr-only">読み込み中…</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3 border-b border-wpborder px-5 py-4">
          {Array.from({ length: columns }, (_, colIndex) => (
            <div
              key={colIndex}
              className="h-4 flex-1 rounded-sm bg-wpborder/60"
              style={{ maxWidth: colIndex === 0 ? '12rem' : undefined }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
}): ReactElement {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <svg
        aria-hidden="true"
        viewBox="0 0 64 64"
        className="h-14 w-14 text-wpborder"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="10" y="14" width="44" height="36" rx="3" />
        <path d="M10 24h44M22 34h20M22 42h12" />
      </svg>
      <p className="text-base font-semibold text-wptext-strong">{title}</p>
      {description && <p className="max-w-md text-wptext-muted">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message?: string | undefined;
  onRetry?: (() => void) | undefined;
}): ReactElement {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 px-4 py-14 text-center">
      <p className="text-base font-semibold text-wptext-strong">情報を読み込めませんでした</p>
      {message && <p className="max-w-md text-wptext-muted">{message}</p>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
        >
          もう一度読み込む
        </button>
      )}
      {/* 何をすればよいか分からず止まってしまうのを防ぐ（D-083） */}
      <p className="mt-2 text-xs text-wptext-muted">
        何度試しても表示されない場合は、事務局にご連絡ください。
      </p>
    </div>
  );
}

export function ForbiddenState({
  message = 'この情報を参照する権限がありません。',
}: {
  message?: string;
}): ReactElement {
  return (
    <div role="alert" className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <p className="text-base font-semibold text-wptext-strong">権限がありません</p>
      <p className="max-w-md text-wptext-muted">{message}</p>
      <p className="mt-2 text-xs text-wptext-muted">
        必要な場合は事務局にロールの付与を依頼してください。
      </p>
    </div>
  );
}
